import { useQueries, useQuery } from "@tanstack/react-query";
import type { WindowPeriod } from "@/domains/Reporting/utils/period";
import { adsService } from "../services/adsService";
import type { AdAnalyticsSummary, AdWindowSummary } from "../types";

export interface AdMetricChange {
  value: number;
  percentage: number;
}

export interface AdMonthComparison {
  impressions: AdMetricChange;
  clicks: AdMetricChange;
  spend: AdMetricChange;
  conversions: AdMetricChange;
  ctr: AdMetricChange | null;
  cpc: AdMetricChange | null;
  cpm: AdMetricChange | null;
}

export interface MonthAdAnalyticsResult {
  current: AdAnalyticsSummary | null;
  previous: AdAnalyticsSummary | null;
  comparison: AdMonthComparison | null;
  isLoading: boolean;
  isError: boolean;
}

const previousMonth = (year: number, month: number): { year: number; month: number } => {
  if (month === 1) return { year: year - 1, month: 12 };
  return { year, month: month - 1 };
};

const change = (current: number, prev: number): AdMetricChange => {
  const value = current - prev;
  const percentage = prev > 0 ? (value / prev) * 100 : 0;
  return { value, percentage };
};

const changeOrNull = (
  current: number | null,
  prev: number | null,
): AdMetricChange | null => {
  if (current == null || prev == null) return null;
  return change(current, prev);
};

/** Flows (impressions, clicks, spend, conversions) compare with the month
 *  before passed through `baseline`, which a month covered only in part
 *  uses to scale them to as many days (`decimals` 2 for money); ratios
 *  compare as they are. */
export const buildAdComparison = (
  current: AdAnalyticsSummary,
  prev: AdAnalyticsSummary,
  baseline: (previous: number, decimals: number) => number = (previous) => previous,
): AdMonthComparison => ({
  impressions: change(current.totals.impressions, baseline(prev.totals.impressions, 0)),
  clicks: change(current.totals.clicks, baseline(prev.totals.clicks, 0)),
  spend: change(current.totals.spend, baseline(prev.totals.spend, 2)),
  conversions: change(current.totals.conversions, baseline(prev.totals.conversions, 0)),
  ctr: changeOrNull(current.totals.ctr, prev.totals.ctr),
  cpc: changeOrNull(current.totals.cpc, prev.totals.cpc),
  cpm: changeOrNull(current.totals.cpm, prev.totals.cpm),
});

/** Fetches month-scoped ad analytics for the requested month plus the
 *  preceding month, and builds a month-over-month comparison block in
 *  the same shape /reporting uses for social-media metric tiles. */
export function useMonthAdAnalytics(
  clientId: string | undefined,
  year: number,
  month: number,
): MonthAdAnalyticsResult {
  const prev = previousMonth(year, month);

  const results = useQueries({
    queries: [
      {
        queryKey: ["ads", "analytics-month", clientId ?? "none", year, month],
        queryFn: () => adsService.getMonthAnalytics(clientId!, year, month),
        enabled: !!clientId,
        staleTime: 60_000,
      },
      {
        queryKey: ["ads", "analytics-month", clientId ?? "none", prev.year, prev.month],
        queryFn: () => adsService.getMonthAnalytics(clientId!, prev.year, prev.month),
        enabled: !!clientId,
        staleTime: 60_000,
      },
    ],
  });

  const [currentQ, prevQ] = results;
  const current = currentQ.data ?? null;
  const previous = prevQ.data ?? null;
  const comparison =
    current && previous ? buildAdComparison(current, previous) : null;

  return {
    current,
    previous,
    comparison,
    isLoading: currentQ.isLoading || prevQ.isLoading,
    isError: currentQ.isError,
  };
}

/** The last 7 or 28 days of Meta spend beside the days before, cached per
 *  client, window and Rome day, so a panel open in two places shares one
 *  request and the window moves on the next day. */
export function useWindowAdAnalytics(
  clientId: string | undefined,
  period: WindowPeriod,
  today: string,
): { data: AdWindowSummary | null; isLoading: boolean; isError: boolean } {
  const query = useQuery({
    queryKey: ["ads", "analytics-window", clientId ?? "none", period, today],
    queryFn: () => adsService.getWindowAnalytics(clientId!, period),
    enabled: !!clientId,
    staleTime: 60_000,
  });
  return { data: query.data ?? null, isLoading: query.isLoading, isError: query.isError };
}
